import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [bootstrapping, setBootstrapping] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setBootstrapping(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        return { ok: true, mfa_required: true, mfa_token: data.mfa_token, email: data.email, expires_in: data.expires_in };
      }
      // Backwards-compat: if MFA somehow disabled and token returns directly
      localStorage.setItem("eyb_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verifyMfa = async (mfa_token, code) => {
    try {
      const { data } = await api.post("/auth/verify-mfa", { mfa_token, code });
      localStorage.setItem("eyb_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const resendMfa = async (mfa_token) => {
    try {
      const { data } = await api.post("/auth/resend-mfa", { mfa_token });
      return { ok: true, mfa_token: data.mfa_token, expires_in: data.expires_in };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("eyb_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, bootstrapping, login, verifyMfa, resendMfa, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
